import Links from '@/shared/constants/navegacao';
import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { DataAmigavel } from '@/shared/utils/datas';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { ReadHolidayResponseDto } from '@/api.types';
import { readHoliday } from './holiday.service';
import { toast } from 'react-toastify';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { HolidayCalendarMonth } from './HolidayCalendarMonth';
import { indexarFeriadosPorDia, formatarPeriodoBloqueio } from './holidays-calendar.utils';
import './holidays-calendar.css';

type Visualizacao = 'calendario' | 'lista';

const MESES_DO_ANO = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] as const;

export default function HolidaysIndexPage() {
  const { setActions } = useBreadcrumbActions();
  const [feriados, setFeriados] = useState<ReadHolidayResponseDto[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [visualizacao, setVisualizacao] = useState<Visualizacao>('calendario');
  const [modalLocaisAberto, setModalLocaisAberto] = useState(false);
  const [feriadoSelecionado, setFeriadoSelecionado] = useState<ReadHolidayResponseDto | null>(null);
  const tableState = useDataTableState('inicio');

  const abrirModalFeriado = useCallback(async (feriado: ReadHolidayResponseDto) => {
    setFeriadoSelecionado(feriado);
    setModalLocaisAberto(true);

    const temLocaisCarregados =
      feriado.geral ||
      (feriado.defensorias?.length ?? 0) > 0 ||
      (feriado.nucleos?.length ?? 0) > 0 ||
      ((feriado as { setores?: unknown[] }).setores?.length ?? 0) > 0;

    if (temLocaisCarregados) return;

    try {
      const [lista] = await readHoliday({
        id: feriado.id,
        skip: 0,
        take: 1,
        comDefensorias: true,
        comNucleos: true,
        comSetores: true,
      } as any);
      if (lista[0]) setFeriadoSelecionado(lista[0]);
    } catch (res) {
      toast.error(String(res));
    }
  }, []);

  const carregarFeriados = useCallback(() => {
    setCarregando(true);
    const precisaLocais = visualizacao === 'lista';

    return readHoliday({
      skip: 0,
      take: 1000,
      fromDate: `${ano}-01-01`,
      toDate: `${ano}-12-31`,
      comDefensorias: precisaLocais,
      comNucleos: precisaLocais,
      comSetores: precisaLocais,
    } as any)
      .then((result) => {
        setFeriados(result[0]);
      })
      .catch((res) => {
        toast.error(String(res));
      })
      .finally(() => {
        setCarregando(false);
      });
  }, [ano, visualizacao]);

  useEffect(() => {
    let cancelled = false;

    setCarregando(true);
    const precisaLocais = visualizacao === 'lista';

    readHoliday({
      skip: 0,
      take: 1000,
      fromDate: `${ano}-01-01`,
      toDate: `${ano}-12-31`,
      comDefensorias: precisaLocais,
      comNucleos: precisaLocais,
      comSetores: precisaLocais,
    } as any)
      .then((result) => {
        if (!cancelled) setFeriados(result[0]);
      })
      .catch((res) => {
        if (!cancelled) toast.error(String(res));
      })
      .finally(() => {
        if (!cancelled) setCarregando(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ano, visualizacao]);

  useEffect(() => {
    setActions({ primary: { label: 'Cadastrar', href: Links.Calendario_Criar, onClick: () => { } } });
    return () => setActions({});
  }, [setActions]);

  const getLocaisVigencia = useCallback((feriado: ReadHolidayResponseDto): string[] => {
    if (feriado.geral) {
      return ['Todos os locais'];
    }
    const nomesDef = feriado.defensorias?.map((d) => d.nome) || [];
    const nomesNuc = feriado.nucleos?.map((n) => n.nome) || [];
    const nomesSet = (feriado as any).setores?.map((s: any) => s.nome) || [];
    return nomesDef.concat(nomesNuc).concat(nomesSet);
  }, []);

  const formatarPeriodo = useCallback((feriado: ReadHolidayResponseDto): string => {
    return formatarPeriodoBloqueio(feriado, DataAmigavel);
  }, []);

  const feriadosPorDia = useMemo(
    () => indexarFeriadosPorDia(feriados, ano),
    [feriados, ano],
  );

  const hoje = useMemo(() => {
    const h = new Date();
    return new Date(h.getFullYear(), h.getMonth(), h.getDate()).getTime();
  }, []);

  const colunas: Column<ReadHolidayResponseDto>[] = useMemo(() => [
    {
      key: 'inicio',
      header: 'Período',
      render: (row) => formatarPeriodo(row),
    },
    { key: 'nome', header: 'Título' },
    {
      key: 'locais',
      header: 'Locais de Vigência',
      render: (row) => {
        const rd = row.defensorias?.length ?? 0;
        const rc = row.nucleos?.length ?? 0;
        const rs = (row as any).setores?.length ?? 0;
        const t = row.geral ? 0 : rd + rc + rs;

        return (
          <>
            {t && t > 0
              ? (
                <button type="button" className="btn btn-link p-0" onClick={() => abrirModalFeriado(row)}>
                  Abrir ({t})
                </button>
              )
              : row.geral ? '(todos)' : ('Nenhuma defensoria/núcleo/setor especificado')
            }
          </>
        );
      },
    },
    {
      key: 'acoes',
      header: 'Ações',
      render: (row) =>
        <Link className="link-secondary" to={Links.Calendario_Editar + '/' + row.id}>
          Editar
        </Link>,
    },
  ], [abrirModalFeriado, formatarPeriodo]);

  const handleAnoChange = (value: string) => {
    const numValue = Number(value);
    if (numValue >= 2000 && numValue <= 9999) {
      setAno(numValue);
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <h3>Bloqueios</h3>
          <span className="blockquote-footer pb-4 text-end">
            Dias indisponíveis.
          </span>
        </div>
      </div>
      <hr />

      <div className="row mb-3">
        <div className="col-md-12">
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <label htmlFor="ano-input" className="mb-0">Ano:</label>
              <input
                id="ano-input"
                type="number"
                className="form-control"
                style={{ width: '100px' }}
                min="2000"
                max="9999"
                value={ano}
                onChange={(e) => handleAnoChange(e.target.value)}
              />
            </div>
            <div className="btn-group ms-auto" role="group">
              <button
                type="button"
                className={`btn ${visualizacao === 'calendario' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setVisualizacao('calendario')}
              >
                Calendário
              </button>
              <button
                type="button"
                className={`btn ${visualizacao === 'lista' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setVisualizacao('lista')}
              >
                Lista
              </button>
            </div>
          </div>
        </div>
      </div>

      {carregando && (
        <div className="text-center text-muted py-4">Carregando bloqueios...</div>
      )}

      {!carregando && visualizacao === 'calendario' && (
        <div className="row">
          {MESES_DO_ANO.map((mes) => (
            <HolidayCalendarMonth
              key={mes}
              mes={mes}
              ano={ano}
              hoje={hoje}
              feriadosPorDia={feriadosPorDia}
              onFeriadoClick={abrirModalFeriado}
            />
          ))}
        </div>
      )}

      {!carregando && visualizacao === 'lista' && (
        <DataTable
          columns={colunas}
          data={feriados}
          initialSortKey="inicio"
          onChange={carregarFeriados}
          serverSide={false}
          states={tableState}
          totalRecords={feriados.length}
          showFilter={false}
          renderFilters={() => <> </>}
        />
      )}

      <Modal show={modalLocaisAberto} onHide={() => setModalLocaisAberto(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            {feriadoSelecionado?.nome}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {feriadoSelecionado && (
            <>
              <div className="mb-3">
                <strong>Período:</strong> {formatarPeriodo(feriadoSelecionado)}
              </div>
              <div className="mb-3">
                <strong>Locais de Vigência:</strong>
                <ul className="mt-2">
                  {getLocaisVigencia(feriadoSelecionado).map((nome, index) => (
                    <li key={index}>{nome}</li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {feriadoSelecionado && (
            <Link
              className="btn btn-primary"
              to={Links.Calendario_Editar + '/' + feriadoSelecionado.id}
            >
              Editar
            </Link>
          )}
          <button type="button" className="btn btn-secondary" onClick={() => setModalLocaisAberto(false)}>
            Fechar
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
